import React, { ReactElement, useState } from 'react';
import { useArgoContext } from '@awsscm/argo-core';
import Footer from '@awsscm/argo-core/argo/ui/footer';
import Header from '@awsscm/argo-core/argo/ui/header';
import AppLayout from '@amzn/awsui-components-react-v3/polaris/app-layout';
import { ApolloProvider } from '@apollo/client';
import { RouteLoadDurationProvider } from '@awsscm/argo-core/argo/utils/metrics/ArgoRouteLoadDurationContext';
import NavBar from '@awsscm/argo-core/argo/ui/navbar';
import getClient from './gql';
import Routes from './Routes';
import './i18n';
import '@amzn/awsui-components-react/index.css';
import './App.css';
import { Notification } from './components/Notification';
import NotificationProvider from './components/Notification/NotificationProvider';
import Navigation from './components/Navigation';
import Help from './help';

/*
 * Argo App Component
 * @component
 */
export default function App(): ReactElement {
  const context = useArgoContext();

  // Adding an Apollo provider high in the component tree makes it available for all children to access.
  // See https://www.apollographql.com/docs/react/get-started/ for more info.
  return (
    <ApolloProvider client={getClient(context)}>
      <div id="awsscm-header">
        <Header />
      </div>
      <AIMApp />
      <div id="awsscm-footer">
        <Footer />
      </div>
    </ApolloProvider>
  );
}

/**
 * AIMApp is a nested function call within App. App contains providers that shouldn't be updated if tools are updated.
 * AIMApp contains the AppLayout which will be updated upon nav bar toggle. Abstracting the applayout component into
 * its own function prevents ApolloProvider queries from being re-fired if the react state changes.
 * @constructor
 */

function AIMApp() {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <NotificationProvider>
      <RouteLoadDurationProvider>
        <AppLayout
          tools={<Help />}
          stickyNotifications
          notifications={<Notification />}
          content={<Routes />}
          disableBodyScroll
          footerSelector="#awsscm-footer"
          headerSelector="#awsscm-header"
          navigation={<NavBar navigationMenu={<Navigation />} />}
          navigationOpen={navOpen}
          onNavigationChange={(e) => setNavOpen(e.detail.open)}
        />
      </RouteLoadDurationProvider>
    </NotificationProvider>
  );
}
